'use client'
import { useEffect, useRef, type RefObject } from 'react'

import type { SheetState } from '../sheet/use-sheet-presence'

interface LauncherFocusOptions {
  panelRef: RefObject<HTMLDivElement | null>
  inputRef: RefObject<HTMLInputElement | null>
  state: SheetState
  /** Search mode focuses the field; a section focuses the dialog itself. */
  isSearch: boolean
  containerId: string
}

/**
 * Moves focus into the launcher once it has dropped (the field in search mode,
 * the dialog otherwise, so Escape and type-anywhere work at once) and, when a
 * keyboard user closes it with Escape, hands focus back to the control that
 * opened it. A close by mouse or by navigating away leaves focus alone.
 */
export const useLauncherFocus = ({ panelRef, inputRef, state, isSearch, containerId }: LauncherFocusOptions) => {
  const present = state !== 'closed'
  const openerRef = useRef<HTMLElement | null>(null)
  const escapedRef = useRef(false)

  useEffect(() => {
    if (!present) {
      return undefined
    }
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    escapedRef.current = false
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        escapedRef.current = true
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      const opener = openerRef.current
      if (escapedRef.current && opener?.isConnected) {
        opener.focus({ preventScroll: true })
      }
    }
  }, [present])

  useEffect(() => {
    if (state !== 'open') {
      return
    }
    if (isSearch) {
      inputRef.current?.focus({ preventScroll: true })
    } else {
      panelRef.current?.focus({ preventScroll: true })
    }
  }, [state, isSearch, containerId, inputRef, panelRef])
}
